import { Patient } from "@cur8/rich-entity";
import { LegalDocument, generateDocumentUrl } from "lib/legal";
import { isInvalidDocumentError, parseDocument } from "lib/legal/parseDocument";
import { useCallback, useMemo } from "react";
import { useConfig } from "render/context/ConfigContext";
import { useLegalDocumentQuery } from "render/hooks/api/queries/useLegalDocumentQuery";
import { useGiveStudyConsentMutation } from "render/hooks/mutations/consent/useGiveStudyConsentMutation";
import { useOptOutStudyConsentMutation } from "render/hooks/mutations/consent/useOptOutStudyConsentMutation";
import { useAsyncHandle } from "render/hooks/useAsyncHandle";
import { LegalDocumentLayout } from "render/ui/layout/LegalDocumentLayout/LegalDocumentLayout";
import { ActionButton } from "render/ui/trigger/ActionButton/ActionButton";
import { CheckInFailView } from "../../../../../../components/ConciergeCheckInSessionView/components/CheckInFailView";
import styles from "./styles.module.sass";
import { Trans } from "./trans";

interface ConsentAutoSummaryDocumentProps {
  patient: Patient;
  onDone: () => void;
}

function formatDocumentBody(content: HTMLElement) {
  const clone = document.createElement(content.tagName);
  clone.innerHTML = content.innerHTML;

  Array.from(clone.getElementsByTagName("h2")).forEach((header, index) => {
    const text = header.innerText;
    const number = index + 1;
    header.innerHTML = `<div class=${styles.numerator}><div>${number
      .toString()
      .padStart(2, "0")}</div><div class=${styles.line}></div></div> ${text}`;
  });

  return clone;
}

export function ConsentAutoSummaryDocument({
  patient,
  onDone,
}: ConsentAutoSummaryDocumentProps) {
  const { appConfig } = useConfig();
  const documentUri = generateDocumentUrl(
    appConfig.legalApiUrl,
    LegalDocument.AutomaticSummary,
    patient.preferredLanguage ?? "sv"
  );
  const giveStudyConsentMutation = useGiveStudyConsentMutation();
  const optOutStudyConsentMutation = useOptOutStudyConsentMutation();
  const legalDocumentQuery = useLegalDocumentQuery(documentUri, {
    select: parseDocument,
  });

  const handleConfirm = useCallback(async () => {
    await giveStudyConsentMutation({
      documentUri,
      consentSignature: {
        $type: "login",
      },
    });
    onDone();
  }, [giveStudyConsentMutation, documentUri, onDone]);

  const handleReject = useCallback(async () => {
    await optOutStudyConsentMutation({ documentUri });
    onDone();
  }, [optOutStudyConsentMutation, onDone, documentUri]);

  const confirmHandler = useAsyncHandle(handleConfirm);
  const rejectHandler = useAsyncHandle(handleReject);

  const isBusy = useMemo(() => {
    return confirmHandler.busy || rejectHandler.busy;
  }, [confirmHandler.busy, rejectHandler.busy]);

  const body = useMemo(() => {
    const content = legalDocumentQuery.data?.body;
    return content ? formatDocumentBody(content) : null;
  }, [legalDocumentQuery.data?.body]);

  const head = legalDocumentQuery.data?.head.innerHTML;

  if (legalDocumentQuery.error) {
    if (isInvalidDocumentError(legalDocumentQuery.error)) {
      return (
        <CheckInFailView
          Header={<Trans.InvalidDocument.Header />}
          Text={<Trans.InvalidDocument.Description />}
        />
      );
    }
    return (
      <CheckInFailView
        Header={<Trans.DocumentLoadFail.Header />}
        Text={<Trans.DocumentLoadFail.Description />}
      />
    );
  }

  return (
    <LegalDocumentLayout
      Description={
        <>
          {head && (
            <div
              className={styles.header}
              dangerouslySetInnerHTML={{
                __html: head,
              }}
            />
          )}
        </>
      }
      Content={
        <>
          {body && (
            <div
              className={styles.body}
              dangerouslySetInnerHTML={{
                __html: body.innerHTML,
              }}
            />
          )}
        </>
      }
      Actions={
        <>
          <ActionButton disabled={isBusy} onClick={confirmHandler.callback}>
            <Trans.CTA.Agree />
          </ActionButton>
          <ActionButton
            disabled={isBusy}
            variant="secondary"
            onClick={rejectHandler.callback}
          >
            <Trans.CTA.Reject />
          </ActionButton>
        </>
      }
    />
  );
}
